// Remove… and Rotate origin secret… of an entry point (spec 11 6.15.10). No secret value is ever shown.
import { useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { isApiError, type Versioned } from '../../api/client';
import { useApi } from '../../api/context';
import { queryKeys, useEntryPointStatus } from '../../api/queries';
import type { EntryPointSettings, Network } from '../../api/types';
import { useToast } from '../../components/toast';
import { Dialog } from '../../components/ui';
import { strings } from '../../strings';

const e = strings.networks.entryPoints;

export function RemoveEntryPointDialog({
  network,
  ep,
  onClose,
  onRemoved,
}: {
  network: Versioned<Network>;
  ep: EntryPointSettings;
  onClose: () => void;
  onRemoved: (message: string) => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const status = useEntryPointStatus(network.data.id, ep.id);
  const [busy, setBusy] = useState(false);
  const origins = [
    ...new Set(
      (status.data?.routes ?? []).flatMap((r) => (r.origin?.deploymentName ? [r.origin.deploymentName] : [])),
    ),
  ];
  const remove = async () => {
    setBusy(true);
    let target = network;
    try {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const saved = await api.patchNetwork(
            network.data.id,
            { settings: { entryPoints: target.data.settings.entryPoints.filter((x) => x.id !== ep.id) } },
            target.etag,
          );
          queryClient.setQueryData(queryKeys.network(network.data.id), saved);
          void queryClient.invalidateQueries({ queryKey: ['network', network.data.id] });
          void queryClient.invalidateQueries({ queryKey: ['networks'] });
          onRemoved(e.removed(network.data.name, ep.id));
          return;
        } catch (error) {
          if (isApiError(error, 'PRECONDITION_FAILED') && attempt === 0) {
            target = await api.getNetwork(network.data.id);
            continue;
          }
          toast.error(error);
          return;
        }
      }
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={e.removeTitle(ep.id)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {strings.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-danger"
            aria-disabled={busy || undefined}
            onClick={() => !busy && void remove()}
            data-autofocus
          >
            {e.removeConfirm}
          </button>
        </>
      }
    >
      <p className="callout callout-danger">{e.removeBody(ep.id)}</p>
      {origins.length > 0 && <p className="mt-2 text-sm">{e.removeOrigins(origins.join(', '))}</p>}
    </Dialog>
  );
}

export function RotateOriginSecretDialog({
  network,
  ep,
  onClose,
}: {
  network: Network;
  ep: EntryPointSettings;
  onClose: () => void;
}) {
  const api = useApi();
  const toast = useToast();
  const queryClient = useQueryClient();
  const ids = useId();
  const status = useEntryPointStatus(network.id, ep.id);
  const secretRoutes = (status.data?.routes ?? []).filter(
    (r) => r.access === 'header' || r.access === 'allow-list-and-header',
  );
  const [routeId, setRouteId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const start = async () => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.rotateOriginSecret(network.id, ep.id, routeId ? { routeId } : {});
      queryClient.setQueryData(queryKeys.entryPointStatus(network.id, ep.id), result);
      void queryClient.invalidateQueries({ queryKey: queryKeys.network(network.id) });
      toast.show({ tone: 'success', title: e.rotationStarted });
      onClose();
    } catch (err) {
      if (isApiError(err, 'ORIGIN_ROTATION_PENDING')) setError(e.codes.ORIGIN_ROTATION_PENDING);
      else if (isApiError(err, 'ORIGIN_SECRET_NOT_USED')) setError(e.codes.ORIGIN_SECRET_NOT_USED);
      else toast.error(err);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={e.rotateTitle(ep.id)}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {strings.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            aria-disabled={busy || secretRoutes.length === 0 || undefined}
            onClick={() => !busy && secretRoutes.length > 0 && void start()}
          >
            {e.startRotation}
          </button>
        </>
      }
    >
      {error && (
        <p className="callout callout-danger mb-3" role="alert" data-testid="rotate-error">
          {error}
        </p>
      )}
      {status.data && secretRoutes.length === 0 ? (
        <p className="help">{e.noSecretRoutes}</p>
      ) : (
        <div className="mb-3">
          <label className="label" htmlFor={`${ids}-route`}>
            {e.rotateRoute}
          </label>
          <select
            id={`${ids}-route`}
            className="input"
            value={routeId}
            onChange={(event) => setRouteId(event.target.value)}
          >
            <option value="">{e.allRoutes}</option>
            {secretRoutes.map((r) => (
              <option key={r.routeId} value={r.routeId}>
                {r.routeId}
              </option>
            ))}
          </select>
        </div>
      )}
      <ol className="list-decimal space-y-1 pl-5 text-sm">
        {e.rotateSteps(network.name).map((step) => (
          <li key={step}>{step}</li>
        ))}
      </ol>
      <p className="help mt-2">{e.rotateNoValue}</p>
    </Dialog>
  );
}
